package com.milta.feature.aicredit.presentation

import com.milta.feature.aicredit.domain.entity.AiCredit
import com.milta.feature.aicredit.domain.usecase.GetMyAiCreditUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.updateAndGet

/**
 * MIL-98 — the caller's own AI credit, shared by every screen showing an AI action (mobile "Ajouter
 * des livraisons" menu and import screens, web import) and by "Mon usage". Koin `single`: each
 * screen calls [refresh] when it opens and after each AI action, so a credit spent from another
 * screen or device is picked up.
 *
 * `null` until the first successful read: the AI actions then stay enabled (fail-open, the server
 * still refuses with `error.ai_credit_exhausted`).
 */
class AiCreditStore(private val getMyCredit: GetMyAiCreditUseCase) {

    private val _credit = MutableStateFlow<AiCredit?>(null)
    val credit: StateFlow<AiCredit?> = _credit.asStateFlow()

    /**
     * Bumped by every [refresh] and by [clear]: only the latest read started writes its result, so
     * an older answer arriving last (two screens reading at once) never overwrites a newer one, and
     * a read started before a logout never writes the previous account's value.
     */
    private val generation = MutableStateFlow(0)

    /** A failed read keeps the last known value. */
    suspend fun refresh(): Result<AiCredit> {
        val started = generation.updateAndGet { it + 1 }
        return getMyCredit().onSuccess { credit ->
            if (generation.value == started) _credit.value = credit
        }
    }

    /** Logout / account switch — never show the previous account's credit. */
    fun clear() {
        generation.update { it + 1 }
        _credit.value = null
    }
}
